import React, { useState, useRef, useEffect } from 'react';
import { Annotation, ClassInfo, Point, ViewTransform } from '../types';
import { getClassColor } from '../utils/colorUtils';
import { X, Trash2, Search } from 'lucide-react';

interface AnnotationEditorProps {
  selectedAnnotation: Annotation | null;
  annotations: Annotation[];
  classes: ClassInfo[];
  transform: ViewTransform;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  onAnnotationUpdate: (id: number, updates: Partial<Annotation>) => void;
  onAnnotationDelete: (id: number) => void;
  onAnnotationSelect: (id: number | null) => void;
}

interface ResizeHandle {
  position: string;
  cursor: string;
  getCoords: (ann: Annotation) => Point;
}

const RESIZE_HANDLES: ResizeHandle[] = [
  { position: 'nw', cursor: 'nw-resize', getCoords: (a) => ({ x: a.x1, y: a.y1 }) },
  { position: 'n', cursor: 'n-resize', getCoords: (a) => ({ x: a.x1 + a.width/2, y: a.y1 }) },
  { position: 'ne', cursor: 'ne-resize', getCoords: (a) => ({ x: a.x2, y: a.y1 }) },
  { position: 'e', cursor: 'e-resize', getCoords: (a) => ({ x: a.x2, y: a.y1 + a.height/2 }) },
  { position: 'se', cursor: 'se-resize', getCoords: (a) => ({ x: a.x2, y: a.y2 }) },
  { position: 's', cursor: 's-resize', getCoords: (a) => ({ x: a.x1 + a.width/2, y: a.y2 }) },
  { position: 'sw', cursor: 'sw-resize', getCoords: (a) => ({ x: a.x1, y: a.y2 }) },
  { position: 'w', cursor: 'w-resize', getCoords: (a) => ({ x: a.x1, y: a.y1 + a.height/2 }) },
];

export const AnnotationEditor: React.FC<AnnotationEditorProps> = ({
  selectedAnnotation,
  annotations,
  classes,
  transform,
  canvasRef,
  onAnnotationUpdate,
  onAnnotationDelete,
  onAnnotationSelect
}) => {
  const [showEditTooltip, setShowEditTooltip] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<Point | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Filter classes based on search
  const filteredClasses = classes.filter(cls =>
    cls.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Open tooltip when annotation is selected
  useEffect(() => {
    if (selectedAnnotation) {
      setShowEditTooltip(true);
    } else {
      setShowEditTooltip(false);
      setSearchTerm('');
    }
  }, [selectedAnnotation]);

  // Calculate tooltip position
  const calculateTooltipPosition = () => {
    if (!selectedAnnotation || !canvasRef.current) return { left: 0, top: 0 };

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    // Convert annotation coords to screen coords
    const annotationScreenX = selectedAnnotation.x1 * transform.scale + transform.x + rect.left;
    const annotationScreenY = selectedAnnotation.y1 * transform.scale + transform.y + rect.top;
    const annotationScreenWidth = selectedAnnotation.width * transform.scale;
    const annotationScreenHeight = selectedAnnotation.height * transform.scale;

    const tooltipWidth = 280;
    const tooltipHeight = 320;
    const padding = 10;

    let left = annotationScreenX + annotationScreenWidth + padding;
    let top = annotationScreenY;

    // Check if tooltip would go off-screen and adjust
    if (left + tooltipWidth > window.innerWidth) {
      left = annotationScreenX - tooltipWidth - padding;
    }
    if (left < 0) {
      left = padding;
    }

    if (top + tooltipHeight > window.innerHeight) {
      top = window.innerHeight - tooltipHeight - padding;
    }
    if (top < padding) {
      top = padding;
    }

    return { left, top };
  };

  // Handle class change
  const handleClassChange = (classId: number) => {
    if (selectedAnnotation) {
      onAnnotationUpdate(selectedAnnotation.id, { class_id: classId });
    }
  };

  // Handle annotation delete
  const handleDelete = () => {
    if (selectedAnnotation) {
      if (window.confirm('Delete this annotation?')) {
        onAnnotationDelete(selectedAnnotation.id);
        setShowEditTooltip(false);
      }
    }
  };

  // Handle mouse down on resize handle
  const handleResizeStart = (e: React.MouseEvent, handle: string) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    setResizeHandle(handle);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  // Handle mouse move for resize
  const handleResizeMove = (e: MouseEvent) => {
    if (!isResizing || !dragStart || !selectedAnnotation || !resizeHandle) return;

    const deltaX = (e.clientX - dragStart.x) / transform.scale;
    const deltaY = (e.clientY - dragStart.y) / transform.scale;

    let newX1 = selectedAnnotation.x1;
    let newY1 = selectedAnnotation.y1;
    let newX2 = selectedAnnotation.x2;
    let newY2 = selectedAnnotation.y2;

    // Update coords based on handle
    if (resizeHandle.includes('w')) newX1 += deltaX;
    if (resizeHandle.includes('n')) newY1 += deltaY;
    if (resizeHandle.includes('e')) newX2 += deltaX;
    if (resizeHandle.includes('s')) newY2 += deltaY;

    // Ensure minimum size
    if (newX2 - newX1 > 10 && newY2 - newY1 > 10) {
      onAnnotationUpdate(selectedAnnotation.id, {
        x1: Math.min(newX1, newX2),
        y1: Math.min(newY1, newY2),
        x2: Math.max(newX1, newX2),
        y2: Math.max(newY1, newY2),
        width: Math.abs(newX2 - newX1),
        height: Math.abs(newY2 - newY1)
      });
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  // Handle mouse up for resize
  const handleResizeEnd = () => {
    setIsResizing(false);
    setResizeHandle(null);
    setDragStart(null);
  };

  // Add global mouse event listeners for resize
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing, dragStart, selectedAnnotation, resizeHandle]);

  // Draw resize handles on canvas overlay
  const renderResizeHandles = () => {
    if (!selectedAnnotation || !canvasRef.current) return null;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    return (
      <div
        style={{
          position: 'fixed',
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
          pointerEvents: 'none',
          zIndex: 999
        }}
      >
        {RESIZE_HANDLES.map(handle => {
          const coords = handle.getCoords(selectedAnnotation);
          const screenX = coords.x * transform.scale + transform.x;
          const screenY = coords.y * transform.scale + transform.y;

          return (
            <div
              key={handle.position}
              style={{
                position: 'absolute',
                left: screenX - 5,
                top: screenY - 5,
                width: 10,
                height: 10,
                backgroundColor: 'white',
                border: '2px solid #2196F3',
                borderRadius: '50%',
                cursor: handle.cursor,
                pointerEvents: 'auto'
              }}
              onMouseDown={(e) => handleResizeStart(e, handle.position)}
            />
          );
        })}
      </div>
    );
  };

  const tooltipPosition = calculateTooltipPosition();

  return (
    <>
      {/* Resize Handles */}
      {selectedAnnotation && renderResizeHandles()}

      {/* Edit Tooltip */}
      {showEditTooltip && selectedAnnotation && (
        <div
          ref={tooltipRef}
          className="annotation-edit-tooltip"
          style={{
            position: 'fixed',
            left: tooltipPosition.left,
            top: tooltipPosition.top,
            width: '280px',
            maxHeight: '320px',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            border: '1px solid #e0e0e0',
            padding: '12px',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-800">Edit Annotation</h4>
            <button
              onClick={() => setShowEditTooltip(false)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-2 top-2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search class..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-blue-400"
            />
          </div>

          {/* Classes List - רשימה מורחבת */}
          <div className="flex-1 overflow-y-auto min-h-0 mb-3" style={{ maxHeight: '180px' }}>
            {filteredClasses.map((cls) => (
              <div
                key={cls.id}
                onClick={() => handleClassChange(cls.id)}
                className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors ${
                  selectedAnnotation.class_id === cls.id
                    ? 'bg-blue-100 text-blue-700'
                    : 'hover:bg-gray-50'
                }`}
              >
                <div
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: getClassColor(cls.id) }}
                />
                <span className="flex-1 text-sm">{cls.name}</span>
                {selectedAnnotation.class_id === cls.id && (
                  <span className="text-xs">✓</span>
                )}
              </div>
            ))}
          </div>

          {/* Info - מידע דחוס */}
          <div className="border-t pt-3 mb-3">
            <div
              className="text-xs text-gray-600"
              style={{
                fontSize: '13px',
                padding: '8px 12px',
                background: '#f5f5f5',
                borderRadius: '6px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              ID: {selectedAnnotation.id} • Pos: ({Math.round(selectedAnnotation.x1)}, {Math.round(selectedAnnotation.y1)}) • Size: {Math.round(selectedAnnotation.width)}×{Math.round(selectedAnnotation.height)}
            </div>
          </div>

          {/* Actions */}
          <div className="border-t pt-3">
            <button
              onClick={handleDelete}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Delete Annotation
            </button>
          </div>
        </div>
      )}
    </>
  );
};